import React, { useEffect, useRef, useState } from 'react';
import { Layer, LayerProps, Source } from 'react-map-gl/mapbox';
import {
  CircleLayerSpecification,
  FillExtrusionLayerSpecification,
  FillLayerSpecification,
  FilterSpecification,
  GeoJSONSourceSpecification,
  LineLayerSpecification,
  MapMouseEvent,
  SymbolLayerSpecification
} from 'mapbox-gl';
import { useMapInstance } from './MapContext';

// One layer of every type is created for each GeoJSONLayer, in this order,
// hidden unless it was given paint. Later layers draw on top, so fill covers
// line covers symbol within one GeoJSONLayer, and sortMapLayers
// (PropertyMap/utilities/mapboxLayerHelpers) reorders by the resulting ids.
const LAYER_TYPES = [
  'symbol',
  'line',
  'fill',
  'fill-extrusion',
  'circle'
] as const;
type LayerType = typeof LAYER_TYPES[number];

const PROP_PREFIX: Record<LayerType, string> = {
  symbol: 'symbol',
  line: 'line',
  fill: 'fill',
  'fill-extrusion': 'fillExtrusion',
  circle: 'circle'
};

const LAYER_EVENTS = {
  OnClick: 'click',
  OnMouseMove: 'mousemove',
  OnMouseEnter: 'mouseenter',
  OnMouseLeave: 'mouseleave',
  OnMouseDown: 'mousedown',
  OnMouseUp: 'mouseup'
} as const;
type EventSuffix = keyof typeof LAYER_EVENTS;
type LayerEventName = typeof LAYER_EVENTS[EventSuffix];

const EVENT_BINDINGS = LAYER_TYPES.flatMap(type =>
  (Object.keys(LAYER_EVENTS) as EventSuffix[]).map(suffix => ({
    prop: `${PROP_PREFIX[type]}${suffix}`,
    type,
    event: LAYER_EVENTS[suffix]
  }))
);

export type LayerEventHandler = (event: MapMouseEvent) => void;

export type GeoJSONData = GeoJSONSourceSpecification['data'];

// Spelled out rather than mapped from LAYER_TYPES: this Prettier predates
// template literal types.
export interface GeoJSONLayerProps {
  id?: string;
  data: GeoJSONData;
  sourceOptions?: Omit<GeoJSONSourceSpecification, 'type' | 'data'>;
  layerOptions?: {
    filter?: FilterSpecification;
    minzoom?: number;
    maxzoom?: number;
  };
  /** Existing layer id to insert this layer's layers before. */
  before?: string;
  fillPaint?: FillLayerSpecification['paint'];
  fillLayout?: FillLayerSpecification['layout'];
  fillOnClick?: LayerEventHandler;
  fillOnMouseMove?: LayerEventHandler;
  fillOnMouseEnter?: LayerEventHandler;
  fillOnMouseLeave?: LayerEventHandler;
  fillOnMouseDown?: LayerEventHandler;
  fillOnMouseUp?: LayerEventHandler;
  linePaint?: LineLayerSpecification['paint'];
  lineLayout?: LineLayerSpecification['layout'];
  lineOnClick?: LayerEventHandler;
  lineOnMouseMove?: LayerEventHandler;
  lineOnMouseEnter?: LayerEventHandler;
  lineOnMouseLeave?: LayerEventHandler;
  lineOnMouseDown?: LayerEventHandler;
  lineOnMouseUp?: LayerEventHandler;
  symbolPaint?: SymbolLayerSpecification['paint'];
  symbolLayout?: SymbolLayerSpecification['layout'];
  symbolOnClick?: LayerEventHandler;
  symbolOnMouseMove?: LayerEventHandler;
  symbolOnMouseEnter?: LayerEventHandler;
  symbolOnMouseLeave?: LayerEventHandler;
  symbolOnMouseDown?: LayerEventHandler;
  symbolOnMouseUp?: LayerEventHandler;
  circlePaint?: CircleLayerSpecification['paint'];
  circleLayout?: CircleLayerSpecification['layout'];
  circleOnClick?: LayerEventHandler;
  circleOnMouseMove?: LayerEventHandler;
  circleOnMouseEnter?: LayerEventHandler;
  circleOnMouseLeave?: LayerEventHandler;
  circleOnMouseDown?: LayerEventHandler;
  circleOnMouseUp?: LayerEventHandler;
  fillExtrusionPaint?: FillExtrusionLayerSpecification['paint'];
  fillExtrusionLayout?: FillExtrusionLayerSpecification['layout'];
  fillExtrusionOnClick?: LayerEventHandler;
  fillExtrusionOnMouseMove?: LayerEventHandler;
  fillExtrusionOnMouseEnter?: LayerEventHandler;
  fillExtrusionOnMouseLeave?: LayerEventHandler;
  fillExtrusionOnMouseDown?: LayerEventHandler;
  fillExtrusionOnMouseUp?: LayerEventHandler;
}

// Shared by every instance so generated ids stay unique and numeric.
let nextLayerId = 0;

const EMPTY_PAINT = {};
const VISIBLE = { visibility: 'visible' as const };
const HIDDEN = { visibility: 'none' as const };

export const GeoJSONLayer = (props: GeoJSONLayerProps) => {
  const { id: idProp, data, sourceOptions, layerOptions, before } = props;
  const map = useMapInstance();
  // Fixed for the life of the component, like the id react-mapbox-gl chose in
  // its constructor. Held in state rather than a memo: sortMapLayers and the
  // event bindings below key on it, and React may discard a memo cache.
  const [id] = useState(() => {
    if (idProp) return idProp;
    nextLayerId += 1;
    return `geojson-${nextLayerId}`;
  });

  const record = (props as unknown) as Record<string, unknown>;
  const latestProps = useRef(record);
  latestProps.current = record;

  const activeBindings = EVENT_BINDINGS.filter(
    binding => typeof record[binding.prop] === 'function'
  );
  const bindingSignature = activeBindings.map(binding => binding.prop).join();

  // Listeners are bound once per set of handler props and dispatch to the
  // latest handler, so a new arrow function per render costs nothing.
  useEffect(() => {
    if (!map) return undefined;
    const listeners = activeBindings.map(({ prop, type, event }) => {
      const layerId = `${id}-${type}`;
      const listener = (mapEvent: MapMouseEvent) => {
        const handler = latestProps.current[prop];
        if (typeof handler === 'function') handler(mapEvent);
      };
      map.on(event as LayerEventName, layerId, listener);
      return { event, layerId, listener };
    });
    return () => {
      listeners.forEach(({ event, layerId, listener }) => {
        map.off(event as LayerEventName, layerId, listener);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, id, bindingSignature]);

  return (
    <Source
      id={id}
      type="geojson"
      data={data as GeoJSONSourceSpecification['data']}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...sourceOptions}
    >
      {LAYER_TYPES.map(type => {
        const prefix = PROP_PREFIX[type];
        const paint =
          (record[`${prefix}Paint`] as object | undefined) ?? EMPTY_PAINT;
        const layout =
          (record[`${prefix}Layout`] as object | undefined) ??
          (Object.keys(paint).length ? VISIBLE : HIDDEN);
        const layer = {
          id: `${id}-${type}`,
          type,
          paint,
          layout,
          beforeId: before,
          ...layerOptions
        } as LayerProps;
        // eslint-disable-next-line react/jsx-props-no-spreading
        return <Layer key={type} {...layer} />;
      })}
    </Source>
  );
};
